/**
 * The workspace's two answers to `exactOptionalPropertyTypes`.
 *
 * That flag separates "the key is missing" from "the key holds `undefined`".
 * The distinction is real and load bearing for a shape that reads a missing key
 * differently from a present-but-empty one — a Mongo-style query document, a
 * patch body, a column mapper. It is noise for a shape whose consumer can never
 * observe the difference. These two helpers name which case a shape is in, so
 * neither one is settled by widening a field to `| undefined` at random.
 */

/**
 * Marks a type as living at a boundary where an absent key and an
 * explicitly-`undefined` key are the same value.
 *
 * The canonical instance is a React props type: JSX gives
 * `props.p === undefined` for `<X />` and for `<X p={undefined} />` alike, so a
 * component can never tell them apart, and the UI library's rule
 * ("`undefined` means use the default; omit the prop") reads them as one. A
 * caller therefore threads a `T | undefined` straight through:
 *
 * ```tsx
 * <Icon name="check" color={row.color} />   // row.color: Color | undefined
 * ```
 *
 * Only the optional members relax. The mapping is homomorphic — one
 * `[K in keyof T]` that carries every modifier through — so a required member
 * stays required (this cannot turn a discriminated union's tag optional), a
 * union member keeps its discriminant literal, and a generic type keeps
 * resolving its members after instantiation. The condition does the work: only
 * a member whose type already admits `undefined` (which is exactly an optional
 * one) gets it added to the value, and an optional member's VALUE type is what
 * `exactOptionalPropertyTypes` checks a passed `undefined` against.
 *
 * It is NOT recursive. A nested object literal inside the shape is its own
 * boundary and needs its own wrapper:
 *
 * ```ts
 * type StageProps = Omittable<{
 *   icon?: Omittable<{ color?: string | null }> | null;
 * }>;
 * ```
 *
 * Wrap the object literal a type authors, not a whole intersection: a mapped
 * type drops call signatures, so the third-party half of an intersection stays
 * outside the wrapper.
 */
export type Omittable<T> = {
  [K in keyof T]: undefined extends T[K] ? T[K] | undefined : T[K];
};

/** `T` with every maybe-`undefined` key turned into a genuinely absent one. */
type Defined<T> = {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
} & {
  [K in keyof T as undefined extends T[K] ? K : never]?: Exclude<
    T[K],
    undefined
  >;
};

/**
 * Deletes the keys holding `undefined`, so an object assembled from optional
 * values can cross into a type that genuinely distinguishes absent from empty.
 *
 * `Omittable` is the answer when the target type is ours to relax. It is the
 * wrong answer when the target means it: a third-party component declares
 * `p?: T` with no `| undefined`, and a repository row means "this column has no
 * value" by carrying no key at all. Under `exactOptionalPropertyTypes` both
 * reject `p: maybeUndefined` — the type says the key may be missing, not that
 * it may hold `undefined`. Passing nothing is what they ask for, and passing
 * nothing is what this does.
 *
 * ```tsx
 * <DayPicker mode="single" {...omitUndefined({ selected, disabled })} />
 * ```
 *
 * ```ts
 * return omitUndefined({ _id: row.id, name: row.name ?? undefined });
 * ```
 *
 * It destroys contextual typing of an inline object literal — the argument is
 * checked against `T extends object`, not against the target. When the literal
 * holds callbacks whose parameters need inferring, annotate first and pass the
 * `const`:
 *
 * ```ts
 * const options: Omittable<SuggestionOptions> = { … };
 * return omitUndefined(options);
 * ```
 *
 * A mapped type cannot be proven to reconstruct its own source, so the return
 * carries the one cast. It is sound: the loop copies exactly the keys the type
 * keeps.
 */
export function omitUndefined<T extends object>(props: T): Defined<T> {
  const defined: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) defined[key] = value;
  }
  return defined as Defined<T>;
}
